import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
} from "./TailwindMuiStubs";
import socket, { connectSocket, disconnectSocket } from "../../socket";
import API_URL from "../../config";

const Results = () => {
    const [crews, setCrews] = useState([]);
    const [categories, setCategories] = useState([]);
    const [marathonTypes, setMarathonTypes] = useState([]);
    const { selectedYear } = useOutletContext();

    useEffect(() => {
        connectSocket();

        const fetchCrews = () => {
            socket.emit("fetchCrews", selectedYear);
        };

        const fetchCategories = () => {
            socket.emit("fetchCategories");
        };

        const fetchMarathonTypes = async () => {
            try {
                const response = await fetch(`${API_URL}/marathon-types`);
                if (response.ok) {
                    const data = await response.json();
                    setMarathonTypes(data);
                } else {
                    console.error("Failed to fetch marathon types");
                }
            } catch (err) {
                console.error("Error fetching marathon types:", err);
            }
        };

        fetchCrews();
        fetchCategories();
        fetchMarathonTypes();

        socket.on("crewsData", (data) => {
            setCrews(data);
        });

        socket.on("categoriesData", (data) => {
            setCategories(data);
        });

        socket.on("crewUpdated", (updatedCrew) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => (crew.id === updatedCrew.id ? updatedCrew : crew))
            );
        });

        return () => {
            disconnectSocket();
            socket.off("crewsData");
            socket.off("categoriesData");
            socket.off("crewUpdated");
        };
    }, [selectedYear]);

    const getFilteredCrews = (marathonTypeId) => {
        const filteredCategories = categories.filter(
            (category) => category.marathon_type_id === marathonTypeId
        );

        return crews.filter(
            (crew) =>
                crew.marathon_year === selectedYear &&
                crew.race_time?.closed &&
                filteredCategories.some((category) => category.id === crew.category?.id)
        );
    };

    const getSortedResults = (filteredCrews) => {
        return [...filteredCrews].sort((a, b) => {
            const timeA = a.race_time?.total_time || "99:99:99";
            const timeB = b.race_time?.total_time || "99:99:99";
            return timeA.localeCompare(timeB);
        });
    };

    const presentCrews = crews.filter((c) => c.present);
    const totalCrewsCount = presentCrews.length;
    const totalParticipantsCount = presentCrews.reduce(
        (sum, c) => sum + (c.participants?.length || 0),
        0
    );

    return (
        <div className="space-y-8">
            <div className="mb-8">
                <Typography variant="h4">Výsledky Maratónu</Typography>
                <Typography variant="body2">
                    Celkové poradie podľa typov maratónu a kategórií pre rok {selectedYear}.
                </Typography>
            </div>

            <div style={{ display: "flex", gap: "20px", marginBottom: "30px" }}>
                <Paper style={{ padding: "20px 40px", textAlign: "center", borderRadius: "16px" }}>
                    <Typography variant="subtitle2" color="textSecondary" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                        Zúčastnené posádky
                    </Typography>
                    <Typography variant="h3" color="primary" style={{ fontWeight: 900 }}>
                        {totalCrewsCount}
                    </Typography>
                </Paper>
                <Paper style={{ padding: "20px 40px", textAlign: "center", borderRadius: "16px" }}>
                    <Typography variant="subtitle2" color="textSecondary" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                        Dokopy ľudí na vode
                    </Typography>
                    <Typography variant="h3" color="primary" style={{ fontWeight: 900 }}>
                        {totalParticipantsCount}
                    </Typography>
                </Paper>
            </div>

            {marathonTypes.map((type) => {
                const filteredCrews = getFilteredCrews(type.id);
                const sortedResults = getSortedResults(filteredCrews);

                const filteredCategories = categories.filter(
                    (category) => category.marathon_type_id === type.id
                );

                return (
                    <div key={type.id} style={{ marginBottom: "40px" }}>
                        {/* Overall Rankings for the Marathon Type */}
                        <Typography variant="h5" gutterBottom>
                            {type.name} - Celkové poradie
                        </Typography>
                        <TableContainer component={Paper} style={{ marginBottom: "20px" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Poradie</TableCell>
                                        <TableCell>Kategória</TableCell>
                                        <TableCell>Mena účastníkov</TableCell>
                                        <TableCell>Výsledný čas</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedResults.map((crew, index) => (
                                        <TableRow key={crew.id}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{crew.category?.name || "N/A"}</TableCell>
                                            <TableCell>
                                                {crew.participants
                                                    .map((participant) => participant.name)
                                                    .join(", ")}
                                            </TableCell>
                                            <TableCell>
                                                {crew.race_time?.total_time || "N/A"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Categories for the Marathon Type */}
                        {filteredCategories.map((category) => {
                            const categoryResults = sortedResults.filter(
                                (crew) => crew.category?.id === category.id
                            );

                            return (
                                <div key={category.id} style={{ marginBottom: "20px" }}>
                                    <Typography variant="h6" gutterBottom>
                                        {category.name}
                                    </Typography>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Poradie</TableCell>
                                                    <TableCell>Mena účastníkov</TableCell>
                                                    <TableCell>Výsledný čas</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {categoryResults.map((crew, index) => (
                                                    <TableRow key={crew.id}>
                                                        <TableCell>{index + 1}</TableCell>
                                                        <TableCell>
                                                            {crew.participants
                                                                .map((participant) => participant.name)
                                                                .join(", ")}
                                                        </TableCell>
                                                        <TableCell>
                                                            {crew.race_time?.total_time || "N/A"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

export default Results;
